import { UseFormRegister, FieldValues, Path, Controller, Control } from "react-hook-form";
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from "@headlessui/react";
import { ChevronDown, Check, Loader2 } from "lucide-react";
import { useState } from "react";
import { useScreenPermission } from "@/hooks/useScreenPermission";
interface DropdownOption {
  id: string | number;
  label: string;
  value: string | number;
}

interface DropdownProps<T extends FieldValues> {
  name: Path<T>;
  label: string;
  options: DropdownOption[];
  register?: UseFormRegister<T>;
  control?: Control<T>;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  isLoading?: boolean;
  onClear?: () => void;
  onChange?: (value: any) => void;
  isMulti?: boolean;
  maxHeight?: string;
  isSchoolDropdown?: boolean;
  onAddOption?: (search: string) => void;
}

export default function Dropdown<T extends FieldValues>({
  name,
  label,
  options,
  register,
  control,
  error,
  required = false,
  disabled = false,
  placeholder = "Select an option",
  isLoading = false,
  isMulti = false,
  onClear,
  onChange,
  maxHeight = "240px", isSchoolDropdown = false, onAddOption,
}: DropdownProps<T>) {
  const [search, setSearch] = useState("");
  const { canCreate } = useScreenPermission("schools");

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(search.toLowerCase())
  );
  if (control) {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          const selected: DropdownOption | DropdownOption[] | undefined = isMulti
            ? options.filter((o) =>
              Array.isArray(field.value)
                ? field.value.includes(o.value)
                : false
            )
            : options.find((o) => String(o.value) === String(field.value));
          const isDisabled = disabled || isLoading;

          return (
            <div>
              <label className="block text-md font-bold text-black mb-1">
                {label} {required && <span className="text-red-500">*</span>}
              </label>

              <Listbox
                value={field.value ?? (isMulti ? [] : "")}
                multiple={isMulti}
                onChange={(val) => {
                  field.onChange(val);
                  setSearch("");
                  if (typeof onChange === "function") {
                    onChange(val);
                  }
                }}
                disabled={isDisabled}
              >
                <ListboxButton
                  className={`w-full flex items-center justify-between px-3 py-3 bg-white border rounded-lg text-sm text-left focus:outline-none data-[focus]:ring-2 data-[focus]:ring-brand-400 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors ${error ? "border-red-500" : "border-gray-300 hover:border-gray-400"
                    }`}
                >
                  <span
                    className={`truncate ${isMulti
                      ? Array.isArray(selected) && selected.length > 0
                        ? "text-gray-900"
                        : "text-gray-400"
                      : selected
                        ? "text-gray-900"
                        : "text-gray-400"
                      }`}
                  >
                    {isLoading
                      ? "Loading..."
                      : isMulti
                        ? Array.isArray(selected) && selected.length > 0
                          ? selected.map((item) => item.label).join(", ")
                          : placeholder
                        : !Array.isArray(selected) && selected
                          ? selected.label
                          : options.length === 0
                            ? "No options available"
                            : placeholder}
                  </span>
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 text-gray-400 animate-spin flex-shrink-0 ml-2" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2 ui-open:rotate-180 transition-transform" />
                  )}
                </ListboxButton>
                <ListboxOptions
  anchor="bottom start"
  style={{
    minHeight: "10px",
    maxHeight: maxHeight,
    overflowY: "auto",
  }}
  className="z-[999999999] w-[var(--button-width)] bg-white border border-gray-200 rounded-lg shadow-xl"
>
                  <div className="sticky top-0 bg-white border-b z-10">
                    <input
                      type="text"
                      value={search}
                      placeholder="Search..."
                      autoComplete="off"
                      className="
    w-full
    px-3
    py-2
    border
    rounded-md
    text-sm
    focus:outline-none
    focus:ring-2
    focus:ring-brand-400
  "
                      onChange={(e) => {
                        setSearch(e.target.value);
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === " ") {
                          e.stopPropagation();
                        }
                        if (e.key === "Enter") {
                          e.preventDefault();
                        }
                      }}
                      onKeyUp={(e) => {
                        e.stopPropagation();
                      }}
                    />
                  </div>
                  <div className="flex flex-col">
                    {/* Scrollable options */}
                    <div className="overflow-y-auto">
                      {filteredOptions.length === 0 ? (
                        <div className="px-3 py-2.5 text-sm text-gray-400 text-center">
                          No options available
                        </div>
                      ) : (
                        filteredOptions.map((opt) => (
                          <ListboxOption
                            key={opt.id}
                            value={opt.value}
                            className="flex items-center justify-between px-3 py-2.5 text-sm cursor-pointer select-none ui-active:bg-brand-50 ui-selected:text-brand-500 ui-selected:font-medium text-gray-800 hover:bg-brand-50"
                          >
                            <span className="truncate">{opt.label}</span>

                            <Check
                              className={`w-3.5 h-3.5 text-brand-500 ${isMulti
                                  ? Array.isArray(field.value) &&
                                    field.value.includes(opt.value)
                                    ? "block"
                                    : "hidden"
                                  : "hidden ui-selected:block"
                                }`}
                            />
                          </ListboxOption>
                        ))
                      )}
                    </div>

                    {isSchoolDropdown && canCreate && (
                      <div className="sticky bottom-0 bg-white border-t p-3 z-10">
                        <button
                          type="button"
                          className="w-full rounded-md bg-orange-50 border border-orange-300 px-3 py-2 text-sm font-semibold text-orange-600 hover:bg-orange-100"
                          onClick={(e) => {
                            e.preventDefault();
                            onAddOption?.(search.trim());
                          }}
                        >
                          {`+ Add ${search ? `"${search}" ` : "New "}School`}
                        </button>
                      </div>
                    )}
                  </div>
                </ListboxOptions>
              </Listbox>

              {error && (
                <div className="flex items-center justify-between mt-1">
                  <p className="text-red-500 text-sm">{error}</p>
                  {onClear && (
                    <button
                      type="button"
                      onClick={onClear}
                      className="text-sm text-brand-500 hover:text-brand-800"
                    >
                      Clear
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        }}
      />
    );
  }

  // Fallback: uncontrolled with register (no portal needed, rare usage)
  return (
    <div>
      <label className="block text-md font-bold text-black mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <select
        {...(register ? register(name) : {})} style={{ maxHeight }}
        disabled={disabled || isLoading || (!isLoading && options.length === 0)}
        className={`w-full px-3 py-3 bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 disabled:bg-gray-100 disabled:cursor-not-allowed ${error ? "border-red-500" : "border-gray-300"
          }`}
      >
        <option value="">
          {isLoading ? "Loading..." : options.length === 0 ? "No options available" : placeholder}
        </option>
        {options.map((option) => (
          <option key={option.id} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error && (
        <div className="flex items-center justify-between mt-1">
          <p className="text-red-500 text-sm">{error}</p>
          {onClear && (
            <button
              type="button"
              onClick={onClear}
              className="text-sm text-brand-500 hover:text-brand-800"
            >
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Helper function to format options from API data
export function formatDropdownOptions(
  data: any[],
  labelKey: string,
  valueKey: string
): DropdownOption[] {
  return data.map((item) => ({
    id: item.id || item[valueKey],
    label: item[labelKey],
    value: item[valueKey],
  }));
}
